import type { MouseEvent } from 'react';
import { useCallback, useMemo } from 'react';

import { apiS3FileIdToUrl } from '#/api/s3File/apiS3FileIdToUrl';
import { useUser } from '#/contexts/UserEnvironmentContext';
import type { CmsApiS3FileLink } from '#/modules/cms/api/types/CmsApiLink';
import { trackClicked } from '#/utils/analytics/track/trackClicked';
import { showModal } from '#/zustand-stores/modalStore';

type Props = {
  eventName: string;
  eventProperties: Record<string, unknown>;
  buttonData: CmsApiS3FileLink;
};

export function useCmsDownloadPermissions(props: Props) {
  const { buttonData, eventName, eventProperties } = props;

  const { user } = useUser();

  const allowDownload = useMemo(
    () => buttonData.data.permission === 'PUBLIC' || Boolean(user),
    [buttonData, user],
  );

  const href = useMemo(
    () =>
      allowDownload
        ? apiS3FileIdToUrl({
            fileId: buttonData.data.s3FileId,
            downloadAttachment: true,
          })
        : undefined,
    [allowDownload, buttonData.data.s3FileId],
  );

  const onClick = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      if (!allowDownload) {
        e.preventDefault();
        e.stopPropagation();
        const redirectTo = `${window.location.pathname}${window.location.search}`;
        showModal('LOGIN', {
          redirectTo,
        });
      } else {
        trackClicked(eventName, {
          url: href,
          is_download_button: true,
          ...eventProperties,
        });
      }
    },
    [href, allowDownload, eventName, eventProperties],
  );

  return { onClick, href, download: true };
}
